import React, { useState, useEffect, useRef } from 'react';
import styles from './CaseLogSymphony.module.scss';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../components/button/Button';
import Modal from '../../../components/modal/Modal';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import EncounterInformationForm from './encounterInformationForm/EncounterInformationForm';
import SkillsForm from './skillsForm/SkillsForm';

const CaseLogSymphony = () => {
  const { logReportId, caseLogId, studentId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const encounterInformationFormRef = useRef();
  const skillsFormRef = useRef();
  const [skillsFormData, setSkillsFormData] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [clinicalSitesList, setClinicalSitesList] = useState([]);
  const [instructorList, setInstructorList] = useState([]);
  const [patientAgeList, setPatientAgeList] = useState([]);
  const [ageRangesList, setAgeRangesList] = useState([]);
  const [patientGenderList, setPatientGenderList] = useState([]);
  const [patientRaceList, setPatientRaceList] = useState([]);
  const [settingList, setSettingList] = useState([]);
  const [encounterTypeList, setEncounterTypeList] = useState([]);
  const [typeOfVisitList, setTypeOfVisitList] = useState([]);
  const [studentParticipationList, setStudentParticipationList] = useState([]);
  const [decisionMakingList, setDecisionMakingList] = useState([]);
  const [student, setStudent] = useState({});
  const [status, setStatus] = useState(null);
  const [activeCaseCount, setActiveCaseCount] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [encounterInformationFormData, setEncounterInformationFormData] = useState({
    clinicalSiteId: null,
    dateOfCase: null,
  });
  const [encounterInformationErrorsCount, setEncounterInformationErrorsCount] = useState(0);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await axiosClient.get('case-log/options', {
          params: studentId ? { studentId } : {}
        });
        if (response.data?.clinicalSites) {
          setClinicalSitesList(response.data.clinicalSites.map((site) => ({
            value: site.id,
            label: site.name,
          })));
        }
        if (response.data?.instructors) {
          setInstructorList(response.data.instructors.map((instructor) => ({
            value: instructor.id,
            label: `${instructor.firstName} ${instructor.lastName}`,
          })));
        }
        if (response.data?.patientAge) {
          setPatientAgeList(response.data.patientAge.map((patientAge) => ({
            value: patientAge.name,
            label: patientAge.name,
            subOptions: patientAge.months ? patientAge.months.map((month) => ({
              value: { year: patientAge.name, month },
              label: month,
            })) : []
          })));
        }
        if (response.data?.ageRanges) {
          setAgeRangesList(response.data.ageRanges.map((ageRange) => ({
            value: ageRange,
            label: ageRange,
          })));
        }
        if (response.data?.patientGender) {
          setPatientGenderList(response.data.patientGender.map((item) => ({
            value: item,
            label: item,
          })));
        }
        if (response.data?.patientRace) {
          setPatientRaceList(response.data.patientRace.map((item) => ({
            value: item,
            label: item,
          })));
        }
        if (response.data?.setting) {
          setSettingList(response.data.setting.map((item) => ({
            value: item,
            label: item,
          })));
        }
        if (response.data?.encounterType) {
          setEncounterTypeList(response.data.encounterType.map((item) => ({
            value: item,
            label: item,
          })));
        }
        if (response.data?.typeOfVisit) {
          setTypeOfVisitList(response.data.typeOfVisit.map((item) => ({
            value: item,
            label: item,
          })));
        }
        if (response.data?.studentParticipation) {
          setStudentParticipationList(response.data.studentParticipation.map((item) => ({
            value: item,
            label: item,
          })));
        }
        if (response.data?.decisionMaking) {
          setDecisionMakingList(response.data.decisionMaking.map((item) => ({
            value: item,
            label: item,
          })));
        }
      } catch (error) {
        showToast(error.message, 'error');
      }
    };
    fetchOptions();
  }, [studentId, showToast]);

  useEffect(() => {
    const fetchCaseLog = async () => {
      setIsLoading(true);
      try {
        const response = await axiosClient.get(`case-log/${caseLogId}`);
        const caseLog = response.data?.caseLog;
        if (caseLog?.student) setStudent(caseLog.student);
        setStatus(caseLog?.status ?? null);
        setActiveCaseCount(response.data?.activeCaseCount ?? 1);
        setEncounterInformationFormData({
          clinicalSiteId: caseLog?.clinicalSite?.id ?? null,
          dateOfCase: caseLog?.dateOfCase ?? null,
          caseCount: caseLog?.caseCount ?? null,
          instructorId: caseLog?.instructor?.id ?? null,
          startTime: caseLog?.startTime ?? '',
          endTime: caseLog?.endTime ?? '',
          patientAge: caseLog?.patientAgeMonth ? { year: caseLog.patientAge, month: caseLog.patientAgeMonth } : caseLog?.patientAge ?? null,
          patientAgeRange: caseLog?.patientAgeRange ?? null,
          patientGender: caseLog?.patientGender ?? null,
          patientRace: caseLog?.patientRace ?? null,
          setting: caseLog?.setting ?? null,
          encounterType: caseLog?.encounterType ?? null,
          typeOfVisit: caseLog?.typeOfVisit ?? null,
          studentParticipation: caseLog?.studentParticipation ?? null,
          decisionMaking: caseLog?.decisionMaking ?? null,
          timeSpentWithPatient: caseLog?.timeSpentWithPatient ?? '',
          timeSpentConsultingWithPreceptor: caseLog?.timeSpentConsultingWithPreceptor ?? '',
          postEncounterDiagnoses: caseLog?.postEncounterDiagnoses ?? '',
          reasonForVisit: caseLog?.reasonForVisit ?? '',
          medications: caseLog?.medications ?? '',
        });
        setSkillsFormData({
          obtainMedicalHistory: caseLog?.obtainMedicalHistory ?? false,
          performPhysicalExamination: caseLog?.performPhysicalExamination ?? false,
          createDifferentialDiagnosis: caseLog?.createDifferentialDiagnosis ?? false,
          deliverOralPresentation: caseLog?.deliverOralPresentation ?? false,
          interpretationOfLaboratoryData: caseLog?.interpretationOfLaboratoryData ?? false,
          interpretationOfRadiographicImaging: caseLog?.interpretationOfRadiographicImaging ?? false,
          creationOfManagementPlan: caseLog?.creationOfManagementPlan ?? false,
          documentationOfEncounter: caseLog?.documentationOfEncounter ?? false,
          facilitateReferralToCommunityResources: caseLog?.facilitateReferralToCommunityResources ?? false,
          abscessIAndD: caseLog?.abscessIAndD ?? false,
          anesthesiaLocalDigitalBlock: caseLog?.anesthesiaLocalDigitalBlock ?? false,
          antepartumFetalAssessment: caseLog?.antepartumFetalAssessment ?? false,
          applyCastSplint: caseLog?.applyCastSplint ?? false,
          applyChangeSterileDressing: caseLog?.applyChangeSterileDressing ?? false,
          asepticAndIsolationTechniques: caseLog?.asepticAndIsolationTechniques ?? false,
          cervicalCulturePAPSmear: caseLog?.cervicalCulturePAPSmear ?? false,
          diabeticFootExam: caseLog?.diabeticFootExam ?? false,
          digitalRectalExamHemocult: caseLog?.digitalRectalExamHemocult ?? false,
          dopplerFetalHeartTones: caseLog?.dopplerFetalHeartTones ?? false,
          dopplerUsAbi: caseLog?.dopplerUsAbi ?? false,
          ekgPerformanceInterpretation: caseLog?.ekgPerformanceInterpretation ?? false,
          fingerStickForGlucose: caseLog?.fingerStickForGlucose ?? false,
          foleyCatheterInsertion: caseLog?.foleyCatheterInsertion ?? false,
          foreignBodyRemoval: caseLog?.foreignBodyRemoval ?? false,
          fundalHeightMeasurement: caseLog?.fundalHeightMeasurement ?? false,
          imIntradermalIVSubcutaneousInjection: caseLog?.imIntradermalIVSubcutaneousInjection ?? false,
          insertNasalPacking: caseLog?.insertNasalPacking ?? false,
          insertNGTube: caseLog?.insertNGTube ?? false,
          ivCatheterInsertion: caseLog?.ivCatheterInsertion ?? false,
          nebulizerTreatment: caseLog?.nebulizerTreatment ?? false,
          peakFlowMeasurement: caseLog?.peakFlowMeasurement ?? false,
          pelvicExam: caseLog?.pelvicExam ?? false,
          pregnancyTest: caseLog?.pregnancyTest ?? false,
          urinalysis: caseLog?.urinalysis ?? false,
          specimenCollection: caseLog?.specimenCollection ?? false,
          stapleSuturePlacement: caseLog?.stapleSuturePlacement ?? false,
          stapleSutureRemoval: caseLog?.stapleSutureRemoval ?? false,
          surgicalIncisionClosure: caseLog?.surgicalIncisionClosure ?? false,
          testicularExam: caseLog?.testicularExam ?? false,
          venipuncture: caseLog?.venipuncture ?? false,
          visualAcuityTesting: caseLog?.visualAcuityTesting ?? false,
          woodsLampFluoresceinEyeExam: caseLog?.woodsLampFluoresceinEyeExam ?? false,
          woundDebridement: caseLog?.woundDebridement ?? false,
          technicalSkillsOther: caseLog?.technicalSkillsOther ?? '',
        });
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCaseLog();
  }, [caseLogId, showToast]);

  const handleSave = async (data) => {
    try {
      await axiosClient.patch(`case-log/symphony/${caseLogId}`, data);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const validateEncounterInformationForm = () => {
    const errorsCount = encounterInformationFormRef.current.validateForm();
    if (errorsCount) {
      setEncounterInformationErrorsCount(errorsCount);
      return false;
    } else {
      setEncounterInformationErrorsCount(0);
      return true;
    }
  };

  const handleValidate = () => {
    setIsValidated(true);
    if (validateEncounterInformationForm()) {
      setShowModal(true);
    }
  };

  useEffect(() => {
    if (isValidated) {
      validateEncounterInformationForm();
    }
  }, [encounterInformationFormData, isValidated]);

  const handleSubmit = async (createNewCaseLog) => {
    setShowModal(false);
    setIsLoading(true);
    try {
      const response = await axiosClient.patch(`log-reports/${logReportId}/submit`, {
        createNewCaseLog,
        caseLogId: parseInt(caseLogId),
      });
      showToast(response.message);
      setIsValidated(false);
      if (response.data?.caseLog) {
        navigate(`/log-reports/${logReportId}/case-logs/${response.data.caseLog.id}`);
      } else {
        navigate('/log-reports');
      }
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const isDisabled = status === 'Completed';

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={studentId ? [
            { label: 'Log Reports', path: '/log-reports' },
            { label: `${student.firstName ?? ''} ${student.lastName ?? ''}`, path: `/log-reports/student/${studentId}` },
            { label: `Log Report - ${logReportId}`, path: `/log-reports/student/${studentId}/log-reports/${logReportId}/case-logs` },
            { label: `Case Log - ${caseLogId}` },
          ] : [
            { label: 'Log Reports', path: '/log-reports' },
            { label: `Log Report - ${logReportId}`, path: `/log-reports/${logReportId}/case-logs` },
            { label: `Case Log - ${caseLogId}` },
          ]}
        />
        {!isDisabled && !isLoading && (
          <Button label="Submit Case" size="medium" onClick={handleValidate} />
        )}
      </div>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3>
              Encounter Information
              {encounterInformationErrorsCount > 0 && (
                <span className={styles.count}>{encounterInformationErrorsCount}</span>
              )}
            </h3>
          </div>
          <div className={styles.cardContent}>
            <EncounterInformationForm
              formData={encounterInformationFormData}
              clinicalSitesList={clinicalSitesList}
              instructorList={instructorList}
              patientAgeList={patientAgeList}
              ageRangesList={ageRangesList}
              patientGenderList={patientGenderList}
              patientRaceList={patientRaceList}
              settingList={settingList}
              encounterTypeList={encounterTypeList}
              typeOfVisitList={typeOfVisitList}
              studentParticipationList={studentParticipationList}
              decisionMakingList={decisionMakingList}
              onSave={(updatedData) => {
                handleSave(updatedData);
                setEncounterInformationFormData((prev) => ({ ...prev, ...updatedData }));
              }}
              ref={encounterInformationFormRef}
              disabled={isDisabled}
            />
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.skillsContent}>
            <SkillsForm
              formData={skillsFormData}
              onSave={(updatedData) => {
                handleSave(updatedData);
                setSkillsFormData((prev) => ({ ...prev, ...updatedData }));
              }}
              ref={skillsFormRef}
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>

      {showModal && (
        <Modal>
          <div className={styles.submitModal}>
            <p className={styles.activeCaseText}>Active Cases: <span>{activeCaseCount}</span></p>
            <h2 className={styles.modalTitle}>Would you like to add another case for the day?</h2>
            <div className={styles.buttons}>
              <Button
                label="No! Submit the Log Report"
                size="small"
                variant="white"
                onClick={() => handleSubmit(false)}
              />
              <Button
                label="Yes & Add New Case"
                size="small"
                onClick={() => handleSubmit(true)}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CaseLogSymphony;
